<template>
  <div class="dashboard-component p-4" :class="{ 'reservation-overview--loading': loading }">
    <h3 class="mb-3">Boekingen - Jaaroverzicht</h3>

    <!-- Filters -->
    <div class="row g-3 align-items-center mb-4">
      <div class="col-auto">
        <select v-model="selectedHouseId" class="form-select">
          <option value="all">Alle huizen</option>
          <option v-for="house in houses" :key="house.id" :value="house.id">
            {{ house.title }}
          </option>
        </select>
      </div>
      <div class="col-auto">
        <select v-model="selectedYear" class="form-select">
          <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
        </select>
      </div>
      <div v-if="displayMode === 'text'" class="col-auto">
        <label class="cancelled-filter">
          <input v-model="includeCancelled" class="cancelled-filter__input" type="checkbox">
          <span class="cancelled-filter__toggle" aria-hidden="true">
            <span class="cancelled-filter__knob"></span>
          </span>
          <span>Geannuleerde tonen</span>
        </label>
      </div>
      <div class="col-auto ms-auto">
        <div class="view-switch" role="group" aria-label="Weergavemodus">
          <button type="button" class="view-switch__button" :class="{ active: displayMode === 'visual' }" aria-label="Visuele weergave" title="Visuele weergave" @click="displayMode = 'visual'">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
          </button>
          <button type="button" class="view-switch__button" :class="{ active: displayMode === 'text' }" aria-label="Tekstweergave" title="Tekstweergave" @click="displayMode = 'text'">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h14M7 12h14M7 19h14"/><circle cx="3" cy="5" r="1"/><circle cx="3" cy="12" r="1"/><circle cx="3" cy="19" r="1"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Loading overlay -->
    <div v-if="loading" class="component-load">Bezig met laden...</div>

    <div class="reservation-total mb-4" aria-live="polite">
      <span>Totaal {{ selectedYear }}</span>
      <strong>{{ selectedYearReservations.length }} {{ reservationLabel(selectedYearReservations.length) }}</strong>
    </div>

    <!-- Calendar per month -->
    <div v-if="selectedHouses.length && displayMode === 'visual'" class="year-calendar" :class="{ 'year-calendar--all-houses': selectedHouseId === 'all' }">
      <div v-for="house in selectedHouses" :key="house.id" class="year-calendar__house mb-5">
        <h4 v-if="selectedHouseId === 'all'">{{ house.title }}</h4>
        <div class="year-calendar__months">
        <div v-for="(monthName, monthIndex) in monthNames" :key="monthIndex" class="month-block mb-4">
        <h5 class="month-block__heading">
          <span>{{ monthName }}</span>
          <strong>{{ reservationsForMonth(house, monthIndex + 1).length }} {{ reservationLabel(reservationsForMonth(house, monthIndex + 1).length) }}</strong>
        </h5>

        <!-- Weekday headers -->
        <div class="year-calendar__header">
          <div v-for="d in 7" :key="d" class="year-calendar__header-day">
            {{ weekdayNames[d - 1] }}
          </div>
        </div>

        <!-- Day grid -->
        <div class="d-flex flex-wrap gap-1">
          <div
              v-for="day in getMonthGrid(monthIndex + 1)"
              :key="day.key"
              class="d-flex align-items-center justify-content-center text-white"
              :class="day.isPlaceholder ? 'bg-light' : [dayColor(house, monthIndex + 1, day.value), { 'reservation-day': getReservations(house, monthIndex + 1, day.value).length, 'reservation-day--highlighted': !isChangeover(house, monthIndex + 1, day.value) && isHighlighted(house, monthIndex + 1, day.value), 'reservation-day--muted': hasHoveredReservation && !isHighlighted(house, monthIndex + 1, day.value) }]"
              @click="!day.isPlaceholder && !isChangeover(house, monthIndex + 1, day.value) && openReservation(house, monthIndex + 1, day.value)"
              @mousemove="!day.isPlaceholder && !isChangeover(house, monthIndex + 1, day.value) && hoverReservation(getReservation(house, monthIndex + 1, day.value), house.id, getBooking(house, monthIndex + 1, day.value).start, $event)"
              @mouseleave="clearHoveredReservation"
              style="flex: 1 0 calc((100% - 6 * 0.25rem) / 7); height: 34px;"
          >
            <template v-if="!day.isPlaceholder && isChangeover(house, monthIndex + 1, day.value)">
              <svg class="reservation-day__split" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" @mouseleave="clearHoveredReservation">
                <polygon
                    points="0,0 57,0 41,100 0,100"
                    fill="#f5a623"
                    :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && !isReservationHighlighted(getDepartureReservation(house, monthIndex + 1, day.value), house.id) }"
                />
                <polygon
                    points="59,0 100,0 100,100 43,100"
                    fill="#f5a623"
                    :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && !isReservationHighlighted(getArrivalReservation(house, monthIndex + 1, day.value), house.id) }"
                />
                <polygon
                    class="reservation-day__split-hitarea"
                    points="0,0 58,0 42,100 0,100"
                    @click.stop="openReservationById(getDepartureReservation(house, monthIndex + 1, day.value).id)"
                    @mousemove.stop="hoverReservation(getDepartureReservation(house, monthIndex + 1, day.value), house.id, getBooking(house, monthIndex + 1, day.value).start, $event)"
                />
                <polygon
                    class="reservation-day__split-hitarea"
                    points="58,0 100,0 100,100 42,100"
                    @click.stop="openReservationById(getArrivalReservation(house, monthIndex + 1, day.value).id)"
                    @mousemove.stop="hoverReservation(getArrivalReservation(house, monthIndex + 1, day.value), house.id, getBooking(house, monthIndex + 1, day.value).start, $event)"
                />
              </svg>
              <span class="reservation-day__number">{{ day.value }}</span>
            </template>
            <template v-else-if="!day.isPlaceholder && isTransition(house, monthIndex + 1, day.value)">
              <svg class="reservation-day__split" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                <polygon
                    points="0,0 57,0 41,100 0,100"
                    :fill="isDeparture(house, monthIndex + 1, day.value) ? '#f5a623' : '#28a745'"
                    :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && isHighlighted(house, monthIndex + 1, day.value) && !isDeparture(house, monthIndex + 1, day.value) }"
                />
                <polygon
                    points="59,0 100,0 100,100 43,100"
                    :fill="isArrival(house, monthIndex + 1, day.value) ? '#f5a623' : '#28a745'"
                    :class="{ 'reservation-day__split-half--faded': hasHoveredReservation && isHighlighted(house, monthIndex + 1, day.value) && !isArrival(house, monthIndex + 1, day.value) }"
                />
              </svg>
              <span class="reservation-day__number">{{ day.value }}</span>
            </template>
            <template v-else>{{ day.isPlaceholder ? '' : day.value }}</template>
            <span v-if="!day.isPlaceholder && hoveredReservation && hoveredHouseId === house.id && hoveredDay === getBooking(house, monthIndex + 1, day.value).start" class="reservation-tooltip">
              {{ reservationTooltip(hoveredReservation) }}
            </span>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>

    <div v-else-if="selectedHouses.length" class="year-calendar year-calendar--text">
      <div v-for="(monthName, monthIndex) in monthNames" :key="monthIndex" class="month-block mb-4">
        <h5 class="reservation-list__heading">
          <span>{{ monthName }}</span>
          <strong>{{ reservationsForMonthAcrossHouses(monthIndex + 1).length }} {{ reservationLabel(reservationsForMonthAcrossHouses(monthIndex + 1).length) }}</strong>
        </h5>
        <ul v-if="reservationsForMonthAcrossHouses(monthIndex + 1).length" class="reservation-list mb-0">
          <li v-for="(reservation, index) in reservationsForMonthAcrossHouses(monthIndex + 1)" :key="reservation.id">
            <button type="button" class="reservation-list__item" @click="openReservationById(reservation.id)">
              {{ index + 1 }}. -> {{ formatDate(reservation.arrivalDate) }} t/m {{ formatDate(reservation.departureDate) }} - {{ reservation.houseTitle }} - {{ reservation.fullName || '-' }} ({{ reservation.agencyName || '-' }}) - {{ formatDate(reservation.addedDate) }} <strong v-if="reservation.cancelled" class="reservation-list__cancelled">Geannuleerd</strong>
            </button>
          </li>
        </ul>
        <p v-else class="text-muted mb-0">Geen reserveringen.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ReservationYearOverview",
  data() {
    const today = new Date();
    const origin = window.location.origin.replace("admin.", "");
    const apiBase = `${origin}/ajax`;

    return {
      apiBase,
      selectedYear: today.getFullYear(),
      displayMode: 'visual',
      includeCancelled: false,
      years: Array.from({ length: 5 }, (_, i) => today.getFullYear() - 1 + i),
      monthNames: [
        "Januari","Februari","Maart","April","Mei","Juni",
        "Juli","Augustus","September","Oktober","November","December"
      ],
      weekdayNames: ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"],

      houses: [],
      selectedHouseId: null,
      loading: false,
      hoveredReservationId: null,
      hoveredReservation: null,
      hoveredHouseId: null,
      hoveredDay: null,
    };
  },
  computed: {
    selectedHouses() {
      return this.selectedHouseId === 'all'
        ? this.houses
        : this.houses.filter(house => house.id === this.selectedHouseId);
    },
    hasHoveredReservation() {
      return Boolean(this.hoveredReservationId);
    },
    selectedYearReservations() {
      const reservations = new Map();

      this.selectedHouses.forEach(house => {
        house.bookings.forEach(booking => {
          this.reservationDetailsForBooking(booking).forEach((reservation, index) => {
            const id = reservation.id || booking.reservations[index];
            const dedupeKey = `${house.id}|${id}`;

            if (id && this.arrivesInSelectedYear(reservation.arrivalDate) && !reservations.has(dedupeKey)) {
              reservations.set(dedupeKey, Object.assign({}, reservation, { id, houseTitle: house.title }));
            }
          });
        });
      });

      return this.sortReservationsByArrival(Array.from(reservations.values()));
    },
  },
  watch: {
    selectedHouseId() {
      this.loadHouseAvailabilities();
    },
    selectedYear() {
      this.loadHouseAvailabilities();
    },
  },
  created() {
    this.loadHouses();
  },
  methods: {
    async loadHouses() {
      try {
        this.clearHoveredReservation();
        this.loading = true;
        const res = await fetch(`${this.apiBase}/house/`);
        const data = await res.json();

        this.houses = data.map(h => ({
          id: h.hashId,
          title: h.title,
          bookings: [],
        }));

        if (this.houses.length) {
          this.selectedHouseId = this.houses[0].id;
          await this.loadHouseAvailabilities();
        }
      } catch (err) {
        console.error("Failed to load houses:", err);
      } finally {
        this.loading = false;
      }
    },

    async loadHouseAvailabilities() {
      const houses = this.selectedHouses;
      if (!houses.length) return;

      this.clearHoveredReservation();
      this.loading = true;

      try {
        const monthsToFetch = [1, 3, 5, 7, 9, 11];
        const fetchPromises = houses.flatMap(house => {
          house.bookings = [];
          return monthsToFetch.map(month => {
          const url = `${this.apiBase}/availability/${house.id}/${month}/${this.selectedYear}`;
          return fetch(url)
              .then(res => res.json())
              .then(data => {
                if (!data || !data.calendar || !data.calendar.length) return;

                data.calendar.forEach(monthData => {
                  if (!monthData.days || !monthData.days.length) return;

                  monthData.days.flat().forEach(d => {
                    if (d && d.date) {
                      house.bookings.push({
                        start: `${d.date.split('-')[0]}-${String(d.date.split('-')[1]).padStart(2,'0')}-${String(d.date.split('-')[2]).padStart(2,'0')}`,
                        end: `${d.date.split('-')[0]}-${String(d.date.split('-')[1]).padStart(2,'0')}-${String(d.date.split('-')[2]).padStart(2,'0')}`,
                        available: d.available,
                        hasArrival: d.hasArrival,
                        hasDeparture: d.hasDeparture,
                        isNotBooked: d.isNotBooked,
                        reservations: d.reservations,
                        reservationDetails: d.reservationDetails || [],
                        cancelledReservationDetails: d.cancelledReservationDetails || [],
                      });
                    }
                  });
                });
              })
              .catch(err => {
                console.error(`Error loading availability for ${house.title}, month ${month}`, err);
              });
          });
        });

        await Promise.all(fetchPromises);

      } finally {
        this.loading = false;
      }
    }
    ,

    daysInMonth(month) {
      return new Date(this.selectedYear, month, 0).getDate();
    },

    getMonthGrid(month) {
      const numDays = this.daysInMonth(month);
      const firstDay = new Date(Date.UTC(this.selectedYear, month - 1, 1)).getUTCDay();

      // Calculate offset for Monday = 0
      const offset = firstDay === 0 ? 6 : firstDay - 1;

      const cells = [];

      // Leading placeholders
      for (let i = 0; i < offset; i++) {
        cells.push({ key: `p-${i}`, isPlaceholder: true });
      }

      // Actual days
      for (let day = 1; day <= numDays; day++) {
        cells.push({ key: `d-${day}`, value: day, isPlaceholder: false });
      }

      // Fill remaining cells
      while (cells.length < 42) {
        cells.push({ key: `a-${cells.length}`, isPlaceholder: true });
      }

      return cells;
    },

    dayColor(house, month, day) {
      const booking = this.getBooking(house, month, day);

      if (!booking) return "bg-loading"; //Added this state to clearly indicate when something goes wrong and no dates are being found
      if (booking.hasArrival && booking.hasDeparture) return "reservation-day--changeover";
      if (booking.hasArrival) return "reservation-day--arrival-only";
      if (booking.hasDeparture) return "reservation-day--departure-only";
      if (!booking.available && !booking.isNotBooked) return "bg-danger";
      return "bg-success";
    },

    getBooking(house, month, day) {
      const dateStr = `${this.selectedYear}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
      return house.bookings.find(b => b.start === dateStr);
    },

    getReservation(house, month, day) {
      return this.getReservations(house, month, day)[0] || null;
    },

    getReservations(house, month, day) {
      const booking = this.getBooking(house, month, day);
      if (!booking) return [];

      return booking.reservationDetails.map((reservation, index) => {
        const id = reservation.id || booking.reservations[index];
        return id ? Object.assign({}, reservation, { id }) : null;
      }).filter(Boolean);
    },

    reservationsForMonth(house, month) {
      const reservations = new Map();
      const prefix = `${this.selectedYear}-${String(month).padStart(2, '0')}-`;

      house.bookings.forEach(booking => {
        if (!booking.start.startsWith(prefix)) return;
        this.reservationDetailsForBooking(booking).forEach((reservation, index) => {
          const id = reservation.id || booking.reservations[index];
          if (id && this.arrivesInMonth(reservation.arrivalDate, month) && !reservations.has(id)) {
            reservations.set(id, Object.assign({}, reservation, { id }));
          }
        });
      });

      return this.sortReservationsByArrival(Array.from(reservations.values()));
    },

    reservationsForMonthAcrossHouses(month) {
      return this.selectedYearReservations.filter(reservation => this.arrivesInMonth(reservation.arrivalDate, month));
    },

    reservationDetailsForBooking(booking) {
      return this.includeCancelled
        ? [...(booking.reservationDetails || []), ...(booking.cancelledReservationDetails || [])]
        : (booking.reservationDetails || []);
    },

    sortReservationsByArrival(reservations) {
      return reservations.sort((a, b) => {
        const arrivalComparison = this.dateSortValue(a.arrivalDate) - this.dateSortValue(b.arrivalDate);
        return arrivalComparison || this.dateSortValue(a.departureDate) - this.dateSortValue(b.departureDate);
      });
    },

    arrivesInMonth(date, month) {
      const [year, arrivalMonth] = String(date || '').slice(0, 10).split('-').map(Number);
      return year === this.selectedYear && arrivalMonth === month;
    },

    arrivesInSelectedYear(date) {
      return Number(String(date || '').slice(0, 4)) === this.selectedYear;
    },

    dateSortValue(date) {
      const [year, month, day] = String(date || '').slice(0, 10).split('-').map(Number);
      return year && month && day ? new Date(year, month - 1, day).getTime() : Number.MAX_SAFE_INTEGER;
    },

    formatDate(date) {
      if (!date) return '-';
      const [year, month, day] = String(date).slice(0, 10).split('-');
      return year && month && day ? `${day}-${month}-${year}` : date;
    },

    reservationLabel(total) {
      return total === 1 ? 'reservatie' : 'reservaties';
    },

    getArrivalReservation(house, month, day) {
      return this.getReservations(house, month, day).find(reservation => reservation.isArrival) || this.getReservations(house, month, day)[0];
    },

    getDepartureReservation(house, month, day) {
      const reservations = this.getReservations(house, month, day);
      return reservations.find(reservation => reservation.isDeparture) || reservations[1] || reservations[0];
    },

    isChangeover(house, month, day) {
      const booking = this.getBooking(house, month, day);
      return booking && booking.hasArrival && booking.hasDeparture && this.getReservations(house, month, day).length > 1;
    },

    isTransition(house, month, day) {
      const booking = this.getBooking(house, month, day);
      return booking && (booking.hasArrival || booking.hasDeparture);
    },

    isArrival(house, month, day) {
      const booking = this.getBooking(house, month, day);
      return booking && booking.hasArrival;
    },

    isDeparture(house, month, day) {
      const booking = this.getBooking(house, month, day);
      return booking && booking.hasDeparture;
    },

    hoverReservation(reservation, houseId, date, event) {
      this.hoveredReservationId = reservation ? reservation.id : null;
      this.hoveredReservation = reservation || null;
      this.hoveredHouseId = reservation ? houseId : null;
      this.hoveredDay = reservation ? date : null;
    },

    clearHoveredReservation() {
      this.hoveredReservationId = null;
      this.hoveredReservation = null;
      this.hoveredHouseId = null;
      this.hoveredDay = null;
    },

    isHighlighted(house, month, day) {
      return house.id === this.hoveredHouseId && this.getReservations(house, month, day).some(reservation => reservation.id === this.hoveredReservationId);
    },

    isReservationHighlighted(reservation, houseId) {
      return reservation && houseId === this.hoveredHouseId && reservation.id === this.hoveredReservationId;
    },

    reservationTooltip(reservation) {
      return [reservation.code, reservation.fullName].filter(Boolean).join(' — ');
    },

    openReservation(house, month, day) {
      const booking = this.getBooking(house, month, day);

      if(booking && booking.reservations[0]){
        this.openReservationById(booking.reservations[0]);
      }
    },

    openReservationById(reservationId) {
      if (reservationId) {
        this.$router.push({ name: "dashboard-form", params: {
            bundle: "reservation",
            definition: "form",
            objectId: reservationId
          } });
      }
    }
  },
};
</script>

<style scoped>
.reservation-day { cursor: pointer; position: relative; transition: filter .22s ease, opacity .22s ease, transform .22s ease; }
.reservation-day--highlighted { box-shadow: inset 0 0 0 3px rgba(33, 37, 41, .9), 0 2px 5px rgba(33, 37, 41, .35); filter: brightness(1.24) saturate(1.15); transform: scale(1.08); z-index: 1; }
.reservation-day--muted { filter: grayscale(.45) opacity(.45) blur(1px); }
.reservation-day--changeover, .reservation-day--arrival-only, .reservation-day--departure-only { background: #fff !important; position: relative; }
.reservation-day__split { bottom: 0; height: 100%; left: 0; position: absolute; top: 0; width: 100%; z-index: 1; }
.reservation-day__split polygon { cursor: pointer; transition: opacity .22s ease; }
.reservation-day__split-hitarea { fill: rgba(0, 0, 0, .001); }
.reservation-day__split-half--faded { opacity: .45; }
.reservation-day__number { pointer-events: none; position: relative; z-index: 3; }
.reservation-overview--loading > :not(.component-load) { filter: blur(3px); pointer-events: none; }
.reservation-tooltip { background: #212529; border-radius: 4px; bottom: calc(100% + 7px); color: #fff; font-size: 12px; left: 50%; padding: 5px 8px; pointer-events: none; position: absolute; transform: translateX(-50%); white-space: nowrap; z-index: 2; }
.reservation-tooltip::after { border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid #212529; content: ''; left: 50%; position: absolute; top: 100%; transform: translateX(-50%); }
.reservation-list { list-style: none; padding-left: 0; }
.reservation-list__heading { align-items: center; background: rgba(108, 117, 125, .08); border: 1px solid rgba(108, 117, 125, .55); border-radius: 4px; display: flex; font-size: 1.05rem; font-weight: 600; justify-content: space-between; margin: 0 0 .75rem; padding: .65rem .85rem; }
.reservation-list__heading strong { color: var(--color-1); font-size: .9rem; }
.year-calendar--text { display: block; }
.year-calendar--text .month-block { width: 100%; }
.reservation-list__item { background: none; border: 0; color: inherit; padding: .25rem 0; text-align: left; }
.reservation-list__item:hover { color: #0d6efd; text-decoration: underline; }
.reservation-list__cancelled { color: #dc3545; margin-left: .4rem; }
.cancelled-filter { align-items: center; cursor: pointer; display: inline-flex; gap: .5rem; white-space: nowrap; }
.cancelled-filter__input { height: 1px; opacity: 0; overflow: hidden; position: absolute; width: 1px; }
.cancelled-filter__toggle { background: #e9ecef; border-radius: 999px; box-sizing: border-box; display: inline-flex; height: 34px; padding: 3px; transition: background-color .2s ease, box-shadow .2s ease; width: 58px; }
.cancelled-filter__knob { background: #fff; border-radius: 50%; box-shadow: 0 1px 3px rgba(0, 0, 0, .2); display: block; height: 28px; transition: transform .2s ease; width: 28px; }
.cancelled-filter__input:checked + .cancelled-filter__toggle { background: var(--color-1); }
.cancelled-filter__input:checked + .cancelled-filter__toggle .cancelled-filter__knob { transform: translateX(24px); }
.cancelled-filter__input:focus-visible + .cancelled-filter__toggle { box-shadow: 0 0 0 3px rgba(var(--color-1-rgb), .25); }
.reservation-total { align-items: center; background: rgba(108, 117, 125, .08); border: 1px solid rgba(108, 117, 125, .3); border-radius: 6px; display: flex; gap: .75rem; justify-content: space-between; padding: .65rem .85rem; }
.reservation-total strong { color: var(--color-1); }
.year-calendar__house { flex: 0 0 100%; width: 100%; }
.year-calendar__months { display: grid; gap: 1.5rem; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); width: 100%; }
.year-calendar__months .month-block { margin-bottom: 0 !important; width: 100% !important; }
.year-calendar__months .d-flex, .year-calendar__months .year-calendar__header { width: 100%; }
.month-block__heading { align-items: center; display: flex; justify-content: space-between; }
.month-block__heading strong { color: var(--color-1); font-size: .9rem; }
.view-switch { background: #e9ecef; border-radius: 999px; display: inline-flex; gap: 2px; padding: 3px; }
.view-switch__button { align-items: center; background: transparent; border: 0; border-radius: 999px; color: #6c757d; display: flex; height: 34px; justify-content: center; padding: 0; width: 38px; }
.view-switch__button.active { background: #fff; box-shadow: 0 1px 3px rgba(0, 0, 0, .2); color: #212529; }
.view-switch__button svg { fill: none; height: 18px; stroke: currentColor; stroke-linecap: round; stroke-width: 2; width: 18px; }
.view-switch__button:first-child svg rect { fill: currentColor; stroke: none; }
</style>
